/**
 * Google Places Autocomplete — script injection + address extraction.
 * Works standalone (no external dependencies beyond the Google Maps JS API).
 */

declare global {
  interface Window {
    google?: {
      maps?: {
        places?: {
          AutocompleteService: new () => any;
          PlacesService: new (el: any) => any;
          PlacesServiceStatus: { OK: string; ZERO_RESULTS: string };
        };
        Map: new (el: HTMLElement) => any;
      };
    };
  }
}

export interface GooglePrediction {
  place_id: string;
  description: string;
  structured_formatting?: { main_text: string; secondary_text: string };
}

export interface GooglePlaceDetails {
  place_id: string;
  formatted_address: string;
  address_components: Array<{ long_name: string; short_name: string; types: string[] }>;
  geometry?: { location: { lat: number; lng: number } };
}

export interface ExtractedAddress {
  subpremise: string;
  streetNumber: string;
  route: string;
  locality: string;
  administrativeAreaLevel1: string;
  administrativeAreaLevel1Long: string;
  administrativeAreaLevel2: string;
  administrativeAreaLevel2Long: string;
  country: string;
  postalCode: string;
  fullStreetAddress: string;
}

export interface FormattedAddress {
  address1: string;
  city: string;
  country: string;
  state: string;
  postal: string;
}

// ---------------------------------------------------------------------------
// Script injection
// ---------------------------------------------------------------------------

let scriptInjected = false;
let scriptReady = false;
const readyCallbacks: Array<() => void> = [];

function onScriptReady() {
  scriptReady = true;
  for (const cb of readyCallbacks) cb();
  readyCallbacks.length = 0;
}

/**
 * Inject the Google Maps JS SDK into the page (idempotent).
 * Resolves once `window.google.maps.places` is available.
 */
export function loadGoogleMapsScript(
  apiKey: string,
  opts: { language?: string; region?: string; version?: string } = {},
): Promise<void> {
  if (scriptReady || window.google?.maps?.places) {
    onScriptReady();
    return Promise.resolve();
  }

  if (scriptInjected) {
    return new Promise<void>((resolve) => readyCallbacks.push(resolve));
  }

  scriptInjected = true;

  return new Promise<void>((resolve, reject) => {
    readyCallbacks.push(resolve);

    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) {
      const poll = () => {
        if (window.google?.maps?.places) { onScriptReady(); }
        else { setTimeout(poll, 100); }
      };
      poll();
      return;
    }

    const params = new URLSearchParams({
      key: apiKey,
      libraries: 'places',
      v: opts.version ?? 'weekly',
    });
    if (opts.language) params.set('language', opts.language);
    if (opts.region) params.set('region', opts.region);

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
    script.async = true;
    script.defer = true;
    script.onload = () => onScriptReady();
    script.onerror = () => reject(new Error('Failed to load Google Maps script'));
    document.head.appendChild(script);
  });
}

// ---------------------------------------------------------------------------
// Core class
// ---------------------------------------------------------------------------

export class GoogleAutocompleteCore {
  /** Search for address predictions. */
  static async searchPlaces(
    input: string,
    options: { types?: string[]; componentRestrictions?: { country: string } } = {},
  ): Promise<GooglePrediction[]> {
    if (!window.google?.maps?.places || !input.trim()) return [];

    const service = new window.google.maps.places.AutocompleteService();
    return new Promise((resolve) => {
      service.getPlacePredictions(
        { input, types: options.types ?? ['address'], componentRestrictions: options.componentRestrictions },
        (predictions: GooglePrediction[] | null, status: string) => {
          resolve(status === window.google?.maps?.places?.PlacesServiceStatus.OK && predictions ? predictions : []);
        },
      );
    });
  }

  /** Get full place details by place_id. */
  static async getPlaceDetails(placeId: string): Promise<GooglePlaceDetails | null> {
    if (!window.google?.maps?.places) return null;

    const svc = new window.google.maps.places.PlacesService(document.createElement('div'));
    return new Promise((resolve) => {
      svc.getDetails(
        { placeId, fields: ['place_id', 'formatted_address', 'address_components', 'geometry'] },
        (place: GooglePlaceDetails | null, status: string) => {
          resolve(status === window.google?.maps?.places?.PlacesServiceStatus.OK && place ? place : null);
        },
      );
    });
  }

  /** Extract structured address fields from a Google PlaceDetails result. */
  static extractAddressComponents(place: GooglePlaceDetails): ExtractedAddress {
    const out: ExtractedAddress = {
      subpremise: '', streetNumber: '', route: '', locality: '',
      administrativeAreaLevel1: '', administrativeAreaLevel1Long: '',
      administrativeAreaLevel2: '', administrativeAreaLevel2Long: '',
      country: '', postalCode: '', fullStreetAddress: '',
    };

    let premise = '';
    let neighborhood = '';
    let sublocality5 = '', sublocality4 = '', sublocality3 = '', sublocality2 = '', sublocality1 = '';
    let postalTown = '';

    for (const c of place.address_components ?? []) {
      const t = c.types;
      if (t.includes('subpremise'))                  out.subpremise = c.long_name;
      if (t.includes('street_number'))               out.streetNumber = c.long_name;
      if (t.includes('premise'))                     premise = c.long_name;
      if (t.includes('route'))                       out.route = c.long_name;
      if (t.includes('neighborhood'))                neighborhood = c.long_name;
      if (t.includes('sublocality_level_5'))         sublocality5 = c.long_name;
      if (t.includes('sublocality_level_4'))         sublocality4 = c.long_name;
      if (t.includes('sublocality_level_3'))         sublocality3 = c.long_name;
      if (t.includes('sublocality_level_2'))         sublocality2 = c.long_name;
      if (t.includes('sublocality_level_1'))         sublocality1 = c.long_name;
      if (t.includes('locality'))                    out.locality = c.long_name;
      if (t.includes('postal_town'))                 postalTown = c.long_name;
      if (t.includes('administrative_area_level_2')) {
        out.administrativeAreaLevel2 = c.short_name;
        out.administrativeAreaLevel2Long = c.long_name;
      }
      if (t.includes('administrative_area_level_1')) {
        out.administrativeAreaLevel1 = c.short_name;
        out.administrativeAreaLevel1Long = c.long_name;
      }
      if (t.includes('country'))      out.country = c.short_name;
      if (t.includes('postal_code'))  out.postalCode = c.long_name;
    }

    // Locality fallbacks (UK postal_town, Asian sublocalities)
    if (!out.locality) out.locality = postalTown || sublocality2 || sublocality1 || '';

    // French-style fallback: use département when région is missing
    if (!out.administrativeAreaLevel1 && out.administrativeAreaLevel2) {
      out.administrativeAreaLevel1 = out.administrativeAreaLevel2Long || out.administrativeAreaLevel2;
      out.administrativeAreaLevel1Long = out.administrativeAreaLevel2Long;
    }

    // Build full street address
    const buildingNumber = out.streetNumber || premise;
    const streetName = out.route || sublocality5 || sublocality4 || sublocality3 || neighborhood;

    if (out.subpremise && buildingNumber && streetName) {
      out.fullStreetAddress = `${out.subpremise}/${buildingNumber} ${streetName}`;
    } else if (buildingNumber && streetName) {
      if (premise && sublocality4 && sublocality3) {
        let addr = `${sublocality3}-${sublocality4}-${premise}`;
        if (sublocality2) addr = `${addr} ${sublocality2}`;
        out.fullStreetAddress = addr;
      } else {
        out.fullStreetAddress = `${buildingNumber} ${streetName}`;
      }
    } else if (streetName) {
      out.fullStreetAddress = streetName;
    } else if (buildingNumber) {
      out.fullStreetAddress = buildingNumber;
    }

    out.fullStreetAddress = out.fullStreetAddress.trim();
    return out;
  }

  /** Shorthand: extract into the flat shape used by address forms. */
  static extractFormattedAddress(place: GooglePlaceDetails): FormattedAddress {
    const a = GoogleAutocompleteCore.extractAddressComponents(place);
    return {
      address1: a.fullStreetAddress,
      city: a.locality,
      country: a.country,
      state: a.administrativeAreaLevel1,
      postal: a.postalCode,
    };
  }
}
